/** Shared components of the awareness panels: the collapsible section and small text blocks (helpers live in shared.ts). */
import { useState, type ReactNode } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

export function Section({
  title,
  icon: Icon,
  badge,
  defaultOpen = true,
  children,
}: {
  title: string;
  icon: LucideIcon;
  badge?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-b border-hairline">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-2 transition hover:bg-overlay"
      >
        <Icon aria-hidden className="h-3.5 w-3.5 shrink-0" />
        <span className="flex-1 truncate">{title}</span>
        {badge}
        {open ? <ChevronDown aria-hidden className="h-3.5 w-3.5 text-ink-3" /> : <ChevronRight aria-hidden className="h-3.5 w-3.5 text-ink-3" />}
      </button>
      {open && <div className="px-3 pb-3 text-xs">{children}</div>}
    </section>
  );
}

export function Muted({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('py-1.5 text-ink-3', className)}>{children}</div>;
}

export function ErrorLine({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-1.5 py-1 leading-snug text-caution">
      <AlertTriangle aria-hidden className="mt-0.5 h-3 w-3 shrink-0" />
      <span>{text}</span>
    </div>
  );
}

export function SubHeading({ children }: { children: ReactNode }) {
  return <div className="mb-1 mt-2 text-[10px] uppercase tracking-wider text-ink-3">{children}</div>;
}
